import { CheckCircleOutline, ChevronRight } from '@mui/icons-material'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { formatCNPJ, maskCpf } from '@/helpers'
import { AccountingCustomerCompany } from '@/types/customer-context'
import { AccountingIcon, CompanyIcon, CreditIcon } from './product-icons'

function PendingDot() {
  return (
    <Box
      aria-label="Pendência"
      sx={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        bgcolor: 'error.main',
      }}
    />
  )
}

function ContextRow({
  icon,
  title,
  name,
  detail,
  current,
  pending,
  onClick,
}: {
  icon: ReactNode
  title: string
  name: string
  detail?: string | null
  current: boolean
  pending: boolean
  onClick: () => void
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '44px minmax(0, 1fr) auto',
        gap: 1.5,
        alignItems: 'center',
        minHeight: 72,
        p: current ? '13px' : '14px',
        borderRadius: 2,
        border: current ? '2px solid' : '1px solid',
        borderColor: current ? 'primary.light' : 'divider',
        bgcolor: current ? 'primary.50' : 'background.paper',
        color: 'text.primary',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'primary.main',
          color: 'secondary.main',
          fontSize: 24,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography noWrap sx={{ fontSize: 13, mt: '2px' }}>
          {name}
        </Typography>
        {detail && (
          <Typography
            noWrap
            sx={{ fontSize: 13, color: 'text.secondary', mt: '2px' }}
          >
            {detail}
          </Typography>
        )}
      </Box>
      {current ? (
        <CheckCircleOutline sx={{ color: 'primary.light', fontSize: 26 }} />
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          {pending && <PendingDot />}
          <ChevronRight sx={{ color: 'text.secondary' }} />
        </Stack>
      )}
    </ButtonBase>
  )
}

export function CreditContextRow({
  current,
  pending,
  name,
  taxId,
  hasApplication,
  onClick,
}: {
  current: boolean
  pending: boolean
  name: string
  taxId: string | null
  hasApplication: boolean
  onClick: () => void
}) {
  const masked = maskCpf(taxId)

  return (
    <ContextRow
      icon={<CreditIcon />}
      title="Crédito"
      name={name || 'Pessoa física'}
      detail={
        hasApplication
          ? masked
            ? `CPF ${masked}`
            : null
          : 'Solicite seu crédito'
      }
      current={current}
      pending={pending}
      onClick={onClick}
    />
  )
}

export function CompanyContextRow({
  company,
  current,
  onClick,
}: {
  company: AccountingCustomerCompany
  current: boolean
  onClick: () => void
}) {
  return (
    <ContextRow
      icon={company.company_key ? <CompanyIcon /> : <AccountingIcon />}
      title="Contabilidade"
      name={company.corporate_name || 'Empresa em abertura'}
      detail={company.tax_id ? `CNPJ ${formatCNPJ(company.tax_id)}` : null}
      current={current}
      pending={!current && company.has_pending}
      onClick={onClick}
    />
  )
}
